'use client'

import { WidgetType } from '@lastbrand/storefront-proto-registry/api/v1/enums'

import { PLBaseErrorBoundary } from '@src/components/pl-base-error-boundary'
import { PLTypedWidgetInterface, PLTypedWidgetProps } from '@src/components/pl-base-widget'
import FaqDs from '@src/components/widgets/faq-ds/faq-ds'
import { FAQSection } from '@src/quince-components/src'
import CUSTOM_SECTION_CONTAINER_NAMES from '@src/quince-site-common/src/constants/customSectionContainerNames'
import { shouldUseDesignSystem } from '@src/utils/should-use-design-system'

import { usePdpSectionImpressionRef, usePlProductProperties } from '../use-ia-impression'
import { toRichText } from '../widget-utils'

import type { FaqItemData, FaqWidgetData } from '@lastbrand/storefront-proto-registry/api/v1/widget'

const FAQ_HEADER_CLASS = 'faq__header'

// Telemetry tokens — module-level consts (not inline literals) so they read as identifiers and stay
// clear of i18next/no-literal-string without suppression comments.
const FAQ_IMPRESSION_ACTION = 'product_faq-section'
const FAQ_TRACKING_ID = 'product-faq-section'
const FAQ_CONTAINER_DISPLAY_NAME = 'Product FAQ Section'

// Hoisted so the object identity stays stable across renders — otherwise a fresh literal each render
// churns the scroll listener in `useTrackIsElementVisible` (a PDP re-renders on color/size selection).
const FAQ_IMPRESSION_CONFIG = {
  action: FAQ_IMPRESSION_ACTION,
  container: {
    contentContainerName: CUSTOM_SECTION_CONTAINER_NAMES.FAQ_SECTION,
    trackingId: FAQ_TRACKING_ID,
    contentContainerDisplayName: FAQ_CONTAINER_DISPLAY_NAME,
  },
}

// Widget.widgets is a recursive type that TypeScript can't resolve through Omit<Widget,'data'>.
// Access via unknown cast so we can read nested FAQ_ITEM data without fighting the type checker.
interface FaqNestedWidget {
  data?: { faqItem?: FaqItemData }
}

const FAQWidgetComponent = (props: PLTypedWidgetProps<FaqWidgetData>) => {
  const faqData = props.data.faqData
  const heading = toRichText(faqData?.heading)

  // IA-only `product_faq-section` impression on scroll-into-view — mirrors the non-PL FaqSection
  // (`entity_id: product_faq-section`, `event_type: is_visible`). PDP-ONLY: this widget also renders on
  // CLP (v1 flat children), so the ref is attached only in the PDP branch below (nested renderedChildren);
  // the CLP branch never attaches it, so the impression never fires off-PDP.
  // `productProperties`: non-PL FaqSection builds it WITHOUT a variant (first color, no price) → 'none'.
  // Off-PDP (CLP) there's no PRODUCT_PURCHASE widget, so this resolves to undefined (harmless).
  const productProperties = usePlProductProperties('none')
  const faqImpressionRef = usePdpSectionImpressionRef(FAQ_IMPRESSION_CONFIG, productProperties)

  // v2 PDP: FAQ_ITEM widgets arrive as nested `widgets[]` (detected via renderedChildren).
  // Read their raw faqItem data and feed to FaqDs — CSS matches v1 exactly (same component).
  const renderedItems = props.renderedChildren ?? []
  if (renderedItems.length) {
    const propsRecord = props as unknown as Record<string, unknown>
    const nestedWidgets: FaqNestedWidget[] = Array.isArray(propsRecord.widgets)
      ? (propsRecord.widgets as FaqNestedWidget[])
      : []

    const accordionItems = nestedWidgets
      .map(w => {
        const item = w.data?.faqItem
        if (!item) return null
        return { header: toRichText(item.header), body: toRichText(item.body) }
      })
      .filter(item => item !== null)

    const sharedProps = {
      title: props.id || '',
      header: heading,
      accordianData: { data: accordionItems },
      boldHeader: true,
      showFirstQuestionExpanded: true,
      usePdpStyling: true,
    } as const

    if (shouldUseDesignSystem()) {
      return (
        <div ref={faqImpressionRef}>
          <FaqDs {...sharedProps} />
        </div>
      )
    }
    return (
      <div ref={faqImpressionRef}>
        <FAQSection {...sharedProps} headerClassName={FAQ_HEADER_CLASS} />
      </div>
    )
  }

  // v1 CLP: FAQ items arrive as flat children[].
  const accordionItems = props.children
    .map(child => {
      const item = child.data?.faqItem
      if (!item) return null
      return { header: toRichText(item.header), body: toRichText(item.body) }
    })
    .filter(item => item !== null)

  const sharedProps = {
    title: props.id || '',
    header: heading,
    accordianData: { data: accordionItems },
    boldHeader: true,
    showFirstQuestionExpanded: true,
  } as const

  if (shouldUseDesignSystem()) {
    return (
      <div>
        <FaqDs {...sharedProps} />
      </div>
    )
  }

  return (
    <div>
      <FAQSection {...sharedProps} headerClassName={FAQ_HEADER_CLASS} />
    </div>
  )
}

export const FAQWidget: PLTypedWidgetInterface<FaqWidgetData, WidgetType.FAQ> = {
  id: WidgetType.FAQ,
  errorBoundary: PLBaseErrorBoundary,
  render: FAQWidgetComponent,
}
